// API Client for CAO Platform

const API_BASE = '/api';

// Generic API request handler with timeout support
async function apiRequest(endpoint, options = {}) {
  const url = `${API_BASE}${endpoint}`;
  const timeout = options.timeout || 30000; // Default 30s timeout

  const config = {
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    ...options,
  };

  // Remove custom options not supported by fetch
  delete config.timeout;

  if (options.body && typeof options.body === 'object') {
    config.body = JSON.stringify(options.body);
  }

  // Create abort controller for timeout
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  config.signal = controller.signal;

  let response;
  try {
    response = await fetch(url, config);
  } catch (err) {
    clearTimeout(timeoutId);
    if (err.name === 'AbortError') {
      throw new Error('Request timed out. Please try again.');
    }
    throw new Error('Network error. Please check your connection and try again.');
  }

  clearTimeout(timeoutId);

  // Handle non-JSON responses (e.g., 504 Gateway Timeout HTML pages)
  let data;
  const contentType = response.headers.get('content-type');

  if (contentType && contentType.includes('application/json')) {
    try {
      data = await response.json();
    } catch (parseError) {
      throw new Error(`Server returned invalid JSON (status: ${response.status})`);
    }
  } else {
    // Non-JSON response - likely an error page
    if (!response.ok) {
      if (response.status === 504) {
        throw new Error('Server is taking too long to respond. Please try again.');
      } else if (response.status === 502) {
        throw new Error('Server is temporarily unavailable. Please try again.');
      } else if (response.status === 503) {
        throw new Error('Service is temporarily unavailable. Please try again.');
      } else {
        throw new Error(`Server error (status: ${response.status}). Please try again.`);
      }
    }
    // If response is ok but not JSON, try to parse anyway
    try {
      data = await response.json();
    } catch (parseError) {
      throw new Error('Unexpected server response format');
    }
  }

  if (!response.ok) {
    throw new Error(data.error || `API request failed (status: ${response.status})`);
  }

  return data;
}

// Contact form submission
export async function submitContactForm(formData) {
  return apiRequest('/contact', {
    method: 'POST',
    body: formData,
  });
}

// Demo request submission
export async function submitDemoRequest(formData) {
  return apiRequest('/demo-request', {
    method: 'POST',
    body: formData,
  });
}

// Newsletter subscription
export async function subscribeNewsletter(email, firstName, lastName) {
  return apiRequest('/newsletter', {
    method: 'POST',
    body: { email, firstName, lastName },
  });
}

// Unsubscribe from newsletter
export async function unsubscribeNewsletter(email) {
  return apiRequest('/newsletter', {
    method: 'DELETE',
    body: { email },
  });
}

// Waitlist signup
export async function joinWaitlist(formData) {
  return apiRequest('/waitlist', {
    method: 'POST',
    body: formData,
  });
}

// Create demo session
export async function createDemoSession(visitorId) {
  return apiRequest('/demo-session', {
    method: 'POST',
    body: { visitorId },
  });
}

// Get demo session
export async function getDemoSession(sessionToken) {
  return apiRequest(`/demo-session?token=${sessionToken}`, {
    method: 'GET',
  });
}

// Send chat message (longer timeout for AI response generation)
export async function sendChatMessage(sessionToken, message) {
  return apiRequest('/chat', {
    method: 'POST',
    body: { sessionToken, message },
    timeout: 60000, // 60s timeout for AI-powered chat
  });
}

// Submit governance request
export async function submitGovernanceRequest(sessionToken, request) {
  return apiRequest('/governance', {
    method: 'POST',
    body: { sessionToken, ...request },
  });
}

// Get governance history
export async function getGovernanceHistory(sessionToken) {
  return apiRequest(`/governance?token=${sessionToken}`, {
    method: 'GET',
  });
}

// Track analytics event
export async function trackEvent(eventType, data = {}) {
  try {
    return apiRequest('/analytics', {
      method: 'POST',
      body: {
        eventType,
        page: window.location.pathname,
        referrer: document.referrer,
        ...data,
      },
    });
  } catch (error) {
    // Silently fail for analytics
    console.warn('Analytics tracking failed:', error);
  }
}

// ============================================
// DIGITAL EMPLOYEE ONBOARDING API
// ============================================

// Get role templates
export async function getRoleTemplates(category = null) {
  const url = category
    ? `/onboarding?action=templates&category=${category}`
    : '/onboarding?action=templates';
  return apiRequest(url, { method: 'GET' });
}

// Start onboarding session
export async function startOnboarding(companyData) {
  return apiRequest('/onboarding?action=start', {
    method: 'POST',
    body: companyData,
  });
}

// Get onboarding session
export async function getOnboardingSession(sessionToken) {
  return apiRequest(`/onboarding?sessionToken=${sessionToken}`, {
    method: 'GET',
  });
}

// Select role in onboarding
export async function selectRole(sessionToken, roleTemplateName, customizations = {}) {
  return apiRequest('/onboarding?action=select-role', {
    method: 'POST',
    body: { sessionToken, roleTemplateName, customizations },
  });
}

// Submit employee details
export async function submitEmployeeDetails(sessionToken, details) {
  return apiRequest('/onboarding?action=employee-details', {
    method: 'POST',
    body: { sessionToken, ...details },
  });
}

// Submit responsibilities
export async function submitResponsibilities(sessionToken, responsibilities) {
  return apiRequest('/onboarding?action=responsibilities', {
    method: 'POST',
    body: { sessionToken, responsibilities },
  });
}

// Submit capabilities
export async function submitCapabilities(sessionToken, data) {
  return apiRequest('/onboarding?action=capabilities', {
    method: 'POST',
    body: { sessionToken, ...data },
  });
}

// Preview governance files
export async function previewGovernanceFiles(sessionToken, complianceFrameworks) {
  return apiRequest('/onboarding?action=governance-preview', {
    method: 'POST',
    body: { sessionToken, complianceFrameworks },
  });
}

// Confirm governance files
export async function confirmGovernance(sessionToken) {
  return apiRequest('/onboarding?action=confirm-governance', {
    method: 'POST',
    body: { sessionToken },
  });
}

// Setup compliance
export async function setupCompliance(sessionToken, data) {
  return apiRequest('/onboarding?action=compliance-setup', {
    method: 'POST',
    body: { sessionToken, ...data },
  });
}

// Configure sub-agents
export async function configureSubAgents(sessionToken, selectedSubAgents) {
  return apiRequest('/onboarding?action=sub-agents', {
    method: 'POST',
    body: { sessionToken, selectedSubAgents },
  });
}

// Complete onboarding
export async function completeOnboarding(sessionToken) {
  return apiRequest('/onboarding?action=complete', {
    method: 'POST',
    body: { sessionToken },
  });
}

// ============================================
// DIGITAL EMPLOYEE MANAGEMENT API
// ============================================

// List digital employees
export async function listDigitalEmployees(filters = {}) {
  const params = new URLSearchParams(filters);
  return apiRequest(`/digital-employees?${params}`, { method: 'GET' });
}

// Get digital employee
export async function getDigitalEmployee(id) {
  return apiRequest(`/digital-employees?id=${id}`, { method: 'GET' });
}

// Update digital employee
export async function updateDigitalEmployee(id, updates) {
  return apiRequest(`/digital-employees?id=${id}`, {
    method: 'PUT',
    body: updates,
  });
}

// Decommission digital employee
export async function decommissionEmployee(id) {
  return apiRequest(`/digital-employees?id=${id}`, { method: 'DELETE' });
}

// ============================================
// GOVERNANCE ENGINE API
// ============================================

// Get governance engine status
export async function getGovernanceEngineStatus(employeeId) {
  return apiRequest(`/governance-engine?employeeId=${employeeId}`, { method: 'GET' });
}

// Start governance engine
export async function startGovernanceEngine(engineId) {
  return apiRequest('/governance-engine?action=start', {
    method: 'POST',
    body: { engineId },
  });
}

// Stop governance engine
export async function stopGovernanceEngine(engineId) {
  return apiRequest('/governance-engine?action=stop', {
    method: 'POST',
    body: { engineId },
  });
}

// Run health check
export async function runHealthCheck(engineId) {
  return apiRequest('/governance-engine?action=health-check', {
    method: 'POST',
    body: { engineId },
  });
}

// Run compliance check
export async function runComplianceCheck(engineId) {
  return apiRequest('/governance-engine?action=compliance-check', {
    method: 'POST',
    body: { engineId },
  });
}

// Get engine logs
export async function getEngineLogs(engineId, options = {}) {
  const params = new URLSearchParams({ id: engineId, action: 'logs', ...options });
  return apiRequest(`/governance-engine?${params}`, { method: 'GET' });
}

// ============================================
// SUB-AGENTS API
// ============================================

// List sub-agents
export async function listSubAgents(employeeId) {
  return apiRequest(`/sub-agents?employeeId=${employeeId}`, { method: 'GET' });
}

// Get sub-agent templates
export async function getSubAgentTemplates() {
  return apiRequest('/sub-agents?templates=true', { method: 'GET' });
}

// Create sub-agent
export async function createSubAgent(data) {
  return apiRequest('/sub-agents?action=create', {
    method: 'POST',
    body: data,
  });
}

// Initialize sub-agent
export async function initializeSubAgent(agentId) {
  return apiRequest('/sub-agents?action=initialize', {
    method: 'POST',
    body: { agentId },
  });
}

// Assign task to sub-agent
export async function assignSubAgentTask(agentId, task) {
  return apiRequest('/sub-agents?action=assign-task', {
    method: 'POST',
    body: { agentId, ...task },
  });
}

// Terminate sub-agent
export async function terminateSubAgent(agentId) {
  return apiRequest(`/sub-agents?id=${agentId}`, { method: 'DELETE' });
}

// ============================================
// BACKGROUND JOBS API
// ============================================

// List background jobs
export async function listBackgroundJobs(employeeId) {
  return apiRequest(`/background-jobs?employeeId=${employeeId}`, { method: 'GET' });
}

// Get job templates
export async function getJobTemplates() {
  return apiRequest('/background-jobs?templates=true', { method: 'GET' });
}

// Create background job
export async function createBackgroundJob(data) {
  return apiRequest('/background-jobs?action=create', {
    method: 'POST',
    body: data,
  });
}

// Run job immediately
export async function runBackgroundJob(jobId) {
  return apiRequest('/background-jobs?action=run', {
    method: 'POST',
    body: { jobId },
  });
}

// Pause job
export async function pauseBackgroundJob(jobId) {
  return apiRequest('/background-jobs?action=pause', {
    method: 'POST',
    body: { jobId },
  });
}

// Resume job
export async function resumeBackgroundJob(jobId) {
  return apiRequest('/background-jobs?action=resume', {
    method: 'POST',
    body: { jobId },
  });
}

export default {
  submitContactForm,
  submitDemoRequest,
  subscribeNewsletter,
  unsubscribeNewsletter,
  joinWaitlist,
  createDemoSession,
  getDemoSession,
  sendChatMessage,
  submitGovernanceRequest,
  getGovernanceHistory,
  trackEvent,
  // Digital Employee Onboarding
  getRoleTemplates,
  startOnboarding,
  getOnboardingSession,
  selectRole,
  submitEmployeeDetails,
  submitResponsibilities,
  submitCapabilities,
  previewGovernanceFiles,
  confirmGovernance,
  setupCompliance,
  configureSubAgents,
  completeOnboarding,
  // Digital Employee Management
  listDigitalEmployees,
  getDigitalEmployee,
  updateDigitalEmployee,
  decommissionEmployee,
  // Governance Engine
  getGovernanceEngineStatus,
  startGovernanceEngine,
  stopGovernanceEngine,
  runHealthCheck,
  runComplianceCheck,
  getEngineLogs,
  // Sub-Agents
  listSubAgents,
  getSubAgentTemplates,
  createSubAgent,
  initializeSubAgent,
  assignSubAgentTask,
  terminateSubAgent,
  // Background Jobs
  listBackgroundJobs,
  getJobTemplates,
  createBackgroundJob,
  runBackgroundJob,
  pauseBackgroundJob,
  resumeBackgroundJob,
};
